import React, { useState } from 'react';

import {
  Footer,
  NextButton,
  PrevButton,
  RegHeader,
  RegShell,
  SectionRow,
  SectionTitle,
  StepCount,
} from 'features/signup/ui/mobileStyles';
import { useFormContext } from 'react-hook-form';

export type RegWizardStep = {
  /** 섹션 타이틀 (중개사무소/숙박업소 정보 등) */
  title: string;
  /** "다음" 누를 때 검증할 필드명들 */
  fields: string[];
  /** 스텝 본문 (각 Step 컴포넌트가 <Body>로 감싼 형태) */
  body: React.ReactNode;
};

type RegisterWizardProps = {
  steps: RegWizardStep[];
  /** 마지막 스텝 "회원가입" (methods.handleSubmit 래핑된 제출) */
  onSubmit: () => void;
};

/**
 * 회원가입 정보입력 모바일 3스텝 위저드 셸.
 * 폼 상태/필드는 부모(RegisterInfoPage)의 FormProvider를 그대로 사용하고,
 * 여기서는 스텝 네비게이션 + 스텝별 검증(trigger)만 담당한다.
 */
function RegisterWizard({ steps, onSubmit }: RegisterWizardProps) {
  const { trigger } = useFormContext();
  const [step, setStep] = useState(1); // 1-based
  const total = steps.length;
  const current = steps[step - 1];
  const isLast = step === total;

  const handleNext = async () => {
    if (isLast) {
      onSubmit();
      return;
    }
    const valid = await trigger(current.fields as never);
    if (valid) {
      setStep((s) => s + 1);
      window.scrollTo({ top: 0 });
    }
  };

  const handlePrev = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0 });
  };

  return (
    <RegShell>
      <RegHeader>회원가입</RegHeader>
      <SectionRow>
        <SectionTitle>{current.title}</SectionTitle>
        <StepCount>
          <b>{step}</b> / {total}
        </StepCount>
      </SectionRow>

      {current.body}

      <Footer>
        {step > 1 && (
          <PrevButton type="button" onClick={handlePrev}>
            이전
          </PrevButton>
        )}
        <NextButton type="button" onClick={handleNext}>
          {isLast ? '회원가입' : '다음'}
        </NextButton>
      </Footer>
    </RegShell>
  );
}

export default RegisterWizard;
